import * as Actions from '../actions';

var initialState = {
	// Loading and errors
	loading: false,
	error: null,
	
	// Credentials list
	credentials: [],
	credentialsByProvider: {},
	availableProviders: [],
	selectedCredential: null,
	
	// Single inventory (not a list!)
	inventory: null,
	inventoryStatus: 'idle', // idle, scanning, completed, failed, never_scanned
	
	// Scanning
	isScanning: false,
	scanError: null,
	
	// Cleanup
	cleanup: {
		isRunning: false,
		isComplete: false,
		isCascade: false,
		progress: {
			completed: 0,
			total: 0,
			currentResourceId: null
		},
		results: []
	},
	
	// Dependencies (for cascade delete warnings)
	dependencies: {
		resourceId: null,
		dependencies: [],
		hasDependencies: false,
		message: null,
		error: null
	},
	
	// Snapshots (historical)
	snapshots: [],
	
	// AI Analysis
	ai: {
		available: null, // null = unknown, true/false = checked
		configured: null, // Is LLM configured in settings?
		connected: null, // Is LLM reachable?
		latency: null, // Connection latency in ms
		provider: null, // Active provider name
		connectionError: null, // Error message if not connected
		statusMessage: null,
		isLoading: false,
		analysis: null,
		timestamp: null,
		cached: false,
		cacheAge: null,
		error: null
	}
};

function cloudAnalyzerReducer(state, action) {
	if (typeof state === 'undefined') state = initialState;
	
	switch (action.type) {
		case Actions.SET_CLOUD_ANALYZER_LOADING:
			return Object.assign({}, state, { loading: action.payload });
			
		case Actions.SET_CLOUD_ANALYZER_ERROR:
			return Object.assign({}, state, { error: action.payload, loading: false });
			
		case Actions.CLEAR_CLOUD_ANALYZER_ERROR:
			return Object.assign({}, state, { error: null });
			
		case Actions.SET_CREDENTIALS: {
			var payload = action.payload;
			var credentials = payload.credentials || [];
			var byProvider = payload.credentialsByProvider || {};
			var providers = payload.availableProviders || [];
			
			return Object.assign({}, state, {
				credentials: credentials,
				credentialsByProvider: byProvider,
				availableProviders: providers
			});
		}
		
		case Actions.SET_SELECTED_CREDENTIAL:
			return Object.assign({}, state, {
				selectedCredential: action.payload,
				// Clear inventory when changing credential
				inventory: null,
				inventoryStatus: 'idle'
			});
			
		case Actions.SET_INVENTORY:
			var inv = action.payload;
			return Object.assign({}, state, {
				inventory: inv,
				inventoryStatus: inv.status || 'idle',
				loading: false
			});
			
		case Actions.SET_INVENTORY_STATUS:
			return Object.assign({}, state, { inventoryStatus: action.payload });
			
		case Actions.CLEAR_INVENTORY:
			return Object.assign({}, state, { inventory: null, inventoryStatus: 'idle' });
			
		case Actions.SCAN_START:
			return Object.assign({}, state, {
				isScanning: true,
				scanError: null,
				inventoryStatus: 'scanning'
			});
			
		case Actions.SCAN_COMPLETE:
			return Object.assign({}, state, {
				isScanning: false,
				inventoryStatus: 'idle'
			});
			
		case Actions.SCAN_FAILED:
			return Object.assign({}, state, {
				isScanning: false,
				scanError: action.payload,
				inventoryStatus: 'failed'
			});
			
		case Actions.CLEANUP_START:
			return Object.assign({}, state, {
				cleanup: {
					isRunning: true,
					isComplete: false,
					progress: {
						completed: 0,
						total: action.payload.total,
						currentResourceId: null
					},
					results: []
				}
			});
			
		case Actions.CLEANUP_PROGRESS:
			return Object.assign({}, state, {
				cleanup: Object.assign({}, state.cleanup, {
					progress: Object.assign({}, state.cleanup.progress, {
						currentResourceId: action.payload.resourceId
					})
				})
			});
			
		case Actions.CLEANUP_RESOURCE_RESULT: {
			var result = action.payload;
			var newResults = state.cleanup.results.concat([result]);
			
			return Object.assign({}, state, {
				cleanup: Object.assign({}, state.cleanup, {
					progress: Object.assign({}, state.cleanup.progress, {
						completed: newResults.length
					}),
					results: newResults
				})
			});
		}
		
		case Actions.CLEANUP_COMPLETE:
			return Object.assign({}, state, {
				cleanup: Object.assign({}, state.cleanup, {
					isRunning: false,
					isComplete: true,
					results: action.payload.results
				})
			});
			
		case Actions.CLEANUP_RESET:
			return Object.assign({}, state, {
				cleanup: {
					isRunning: false,
					isComplete: false,
					isCascade: false,
					progress: {
						completed: 0,
						total: 0,
						currentResourceId: null
					},
					results: []
				}
			});
		
		case Actions.SET_DEPENDENCIES:
			return Object.assign({}, state, {
				dependencies: {
					resourceId: action.payload.resourceId,
					dependencies: action.payload.dependencies || [],
					hasDependencies: action.payload.hasDependencies || false,
					message: action.payload.message,
					error: action.payload.error || null
				}
			});
			
		case Actions.CLEAR_DEPENDENCIES:
			return Object.assign({}, state, {
				dependencies: {
					resourceId: null,
					dependencies: [],
					hasDependencies: false,
					message: null,
					error: null
				}
			});
			
		case Actions.SET_SNAPSHOTS:
			return Object.assign({}, state, { snapshots: action.payload });
		
		// AI Analysis actions
		case Actions.AI_STATUS_SET:
			return Object.assign({}, state, {
				ai: Object.assign({}, state.ai, {
					available: action.payload.available,
					configured: action.payload.configured,
					connected: action.payload.connected,
					latency: action.payload.latency,
					provider: action.payload.provider,
					connectionError: action.payload.error,
					statusMessage: action.payload.message
				})
			});
			
		case Actions.AI_ANALYSIS_START:
			return Object.assign({}, state, {
				ai: Object.assign({}, state.ai, {
					isLoading: true,
					error: null
				})
			});
			
		case Actions.AI_ANALYSIS_SUCCESS:
			return Object.assign({}, state, {
				ai: Object.assign({}, state.ai, {
					isLoading: false,
					analysis: action.payload.analysis,
					timestamp: action.payload.timestamp,
					cached: action.payload.cached || false,
					cacheAge: action.payload.age || null,
					error: null
				})
			});
			
		case Actions.AI_ANALYSIS_FAILED:
			return Object.assign({}, state, {
				ai: Object.assign({}, state.ai, {
					isLoading: false,
					error: action.payload
				})
			});
			
		case Actions.AI_ANALYSIS_CLEAR:
			return Object.assign({}, state, {
				ai: Object.assign({}, state.ai, {
					analysis: null,
					timestamp: null,
					cached: false,
					cacheAge: null,
					error: null
				})
			});
			
		default:
			return state;
	}
}

export default cloudAnalyzerReducer;
